/**
 * Escalas por variable del EBR (Actividad Vulnerable). Componente PRESENTACIONAL: recibe las
 * escalas vigentes y devuelve el objeto completo al contenedor, que es quien hace el PUT a
 * `/api/riesgo/metodologia`. Mismas claves y niveles que valida `riesgo._sanitize_escalas`.
 */
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal } from "lucide-react";

export const DEFAULT_ESCALAS_AV = {
  tipo_persona: {
    fisica_nacional: "bajo", fisica_extranjera: "medio",
    moral_nacional: "medio", moral_extranjera: "alto", fideicomiso: "alto",
  },
  monto_frecuencia: { corte_medio: 50000, corte_alto: 250000, unidad: "MXN/mes" },
  uso_efectivo: { corte_medio_pct: 15, corte_alto_pct: 40 },
  medio_pago: { efectivo: "alto", transferencia: "bajo", cheque: "medio", tarjeta: "bajo", domiciliacion: "bajo" },
  canal: { presencial: "bajo", no_presencial: "medio", digital: "medio", corresponsal: "medio", app: "alto" },
};

const NIVELES = ["bajo", "medio", "alto"];
const NIVEL_COLOR = {
  bajo: "bg-emerald-100 text-emerald-700",
  medio: "bg-amber-100 text-amber-700",
  alto: "bg-red-100 text-red-700",
};

const TP = [
  { key: "fisica_nacional", label: "Persona física nacional" },
  { key: "fisica_extranjera", label: "Persona física extranjera" },
  { key: "moral_nacional", label: "Persona moral nacional" },
  { key: "moral_extranjera", label: "Persona moral extranjera" },
  { key: "fideicomiso", label: "Fideicomiso" },
];
const MP = [
  { key: "efectivo", label: "Efectivo" },
  { key: "transferencia", label: "Transferencia" },
  { key: "cheque", label: "Cheque" },
  { key: "tarjeta", label: "Tarjeta" },
  { key: "domiciliacion", label: "Domiciliación" },
];
const CANAL = [
  { key: "presencial", label: "Presencial" },
  { key: "no_presencial", label: "No presencial" },
  { key: "digital", label: "Digital" },
  { key: "corresponsal", label: "Corresponsal" },
  { key: "app", label: "App" },
];

function BloqueNiveles({ titulo, testid, items, valores, onSet, disabled }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid={testid}>
      <p className="mb-2 text-sm font-medium text-slate-800">{titulo}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(({ key, label }) => (
          <div key={key} className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid={`${testid}-${key}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-600">{label}</span>
              <Badge className={NIVEL_COLOR[valores[key]] || ""}>{valores[key]}</Badge>
            </div>
            <select
              className="mt-1.5 h-8 w-full rounded border border-slate-300 px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
              data-testid={`${testid}-${key}-select`} value={valores[key]} disabled={disabled}
              onChange={(e) => onSet(key, e.target.value)}>
              {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AvEbrEscalas({ escalas, onChange, disabled }) {
  const set = (bloque, key, val) => onChange({ ...escalas, [bloque]: { ...escalas[bloque], [key]: val } });
  const setNum = (bloque, key, val) => set(bloque, key, Number(val));

  return (
    <div className="space-y-3" data-testid="av-ebr-escalas">
      <p className="flex items-center gap-2 text-sm font-medium text-slate-800">
        <SlidersHorizontal className="h-4 w-4 text-primary" /> Escalas por variable (cómo se convierte el dato en nivel de riesgo)
      </p>

      <BloqueNiveles titulo="Tipo de persona" testid="av-escala-tp" items={TP}
        valores={escalas.tipo_persona} disabled={disabled}
        onSet={(k, v) => set("tipo_persona", k, v)} />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="av-escala-mf">
          <p className="mb-2 text-sm font-medium text-slate-800">Monto y frecuencia esperada ({escalas.monto_frecuencia.unidad})</p>
          <div className="flex flex-wrap gap-3">
            <div>
              <p className="mb-1 text-xs text-slate-500">Medio desde</p>
              <Input type="number" min="0" className="h-8 w-32" disabled={disabled}
                data-testid="av-escala-mf-medio" value={escalas.monto_frecuencia.corte_medio}
                onChange={(e) => setNum("monto_frecuencia", "corte_medio", e.target.value)} />
            </div>
            <div>
              <p className="mb-1 text-xs text-slate-500">Alto desde</p>
              <Input type="number" min="0" className="h-8 w-32" disabled={disabled}
                data-testid="av-escala-mf-alto" value={escalas.monto_frecuencia.corte_alto}
                onChange={(e) => setNum("monto_frecuencia", "corte_alto", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="av-escala-ue">
          <p className="mb-2 text-sm font-medium text-slate-800">Uso de efectivo (% del volumen)</p>
          <div className="flex flex-wrap gap-3">
            <div>
              <p className="mb-1 text-xs text-slate-500">Medio desde</p>
              <Input type="number" min="0" max="100" className="h-8 w-28" disabled={disabled}
                data-testid="av-escala-ue-medio" value={escalas.uso_efectivo.corte_medio_pct}
                onChange={(e) => setNum("uso_efectivo", "corte_medio_pct", e.target.value)} />
            </div>
            <div>
              <p className="mb-1 text-xs text-slate-500">Alto desde</p>
              <Input type="number" min="0" max="100" className="h-8 w-28" disabled={disabled}
                data-testid="av-escala-ue-alto" value={escalas.uso_efectivo.corte_alto_pct}
                onChange={(e) => setNum("uso_efectivo", "corte_alto_pct", e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <BloqueNiveles titulo="Medio de pago" testid="av-escala-mp" items={MP}
        valores={escalas.medio_pago} disabled={disabled}
        onSet={(k, v) => set("medio_pago", k, v)} />

      <BloqueNiveles titulo="Canal de contratación (factor Canal · Acuerdo 115/2026)" testid="av-escala-canal"
        items={CANAL} valores={escalas.canal} disabled={disabled}
        onSet={(k, v) => set("canal", k, v)} />
    </div>
  );
}
